import React, { useState, useEffect } from 'react';
import { Network, Save, X } from 'lucide-react';

const COLORS = [
  '#2563EB', '#7C3AED', '#DB2777', '#DC2626',
  '#D97706', '#16A34A', '#0891B2', '#0284C7',
  '#6366F1', '#8B5CF6', '#EC4899', '#64748B'
];

const TIPOS = [
  { value: 'division',  label: 'División'  },
  { value: 'sucursal',  label: 'Sucursal'  },
  { value: 'sede',      label: 'Sede'      },
  { value: 'region',    label: 'Región'    },
  { value: 'area',      label: 'Área'      },
  { value: 'holding',   label: 'Holding'   },
  { value: 'unidad',    label: 'Unidad'    },
];

export default function OrgUnitForm({ unit, orgUnits = [], onSave, onCancel }) {
  const [formData, setFormData] = useState({
    nombre:      '',
    tipo:        'division',
    parent_id:   '',
    descripcion: '',
    responsable: '',
    ubicacion:   '',
    color:       '#2563EB',
  });

  useEffect(() => {
    if (unit) {
      setFormData({
        nombre:      unit.nombre      || '',
        tipo:        unit.tipo        || 'division',
        parent_id:   unit.parent_id   || '',
        descripcion: unit.descripcion || '',
        responsable: unit.responsable || '',
        ubicacion:   unit.ubicacion   || '',
        color:       unit.color       || '#2563EB',
      });
    }
  }, [unit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      alert('El nombre es obligatorio');
      return;
    }
    if (!formData.tipo) {
      alert('El tipo es obligatorio');
      return;
    }
    // Enviar parent_id como null si está vacío
    onSave({ ...formData, parent_id: formData.parent_id || null });
  };

  // Filtrar la unidad siendo editada de las opciones de parent (evitar auto-referencia)
  const parentOptions = orgUnits.filter(u => u.id !== unit?.id);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-blue-100">
            <Network className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800">
            {unit ? 'Editar' : 'Nueva'} Unidad Organizativa
          </h3>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

          {/* Nombre */}
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nombre *
            </label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="ej: Sucursal Norte, Sede Central, División Operaciones"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              required
            />
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tipo *
            </label>
            <select
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none bg-white"
              required
            >
              {TIPOS.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Pertenece a (parent_id) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Pertenece a <span className="font-normal text-gray-400">(opcional)</span>
            </label>
            <select
              name="parent_id"
              value={formData.parent_id}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none bg-white"
            >
              <option value="">— Ninguna (unidad raíz) —</option>
              {parentOptions.map(u => (
                <option key={u.id} value={u.id}>
                  {u.parent_nombre ? `${u.parent_nombre} → ${u.nombre}` : u.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Responsable */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Responsable
            </label>
            <input
              type="text"
              name="responsable"
              value={formData.responsable}
              onChange={handleChange}
              placeholder="Nombre del responsable"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>

          {/* Ubicación */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Ubicación
            </label>
            <input
              type="text"
              name="ubicacion"
              value={formData.ubicacion}
              onChange={handleChange}
              placeholder="Ciudad, edificio, dirección"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>

          {/* Color */}
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Color representativo
            </label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, color }))}
                  className={`w-12 h-12 rounded-lg border-4 transition-all ${
                    formData.color === color ? 'border-gray-800 scale-110' : 'border-gray-200'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Descripción */}
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción de esta unidad organizativa"
              rows="3"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="submit"
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-green-700 transition-colors"
          >
            <Save className="w-5 h-5" />
            {unit ? 'Actualizar' : 'Guardar'} Unidad
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-gray-400 transition-colors"
          >
            <X className="w-5 h-5" />
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
